import { useMemo, useState } from 'react';

import {
  Chip,
  Alert,
  Paper,
  Stack,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  TableContainer,
  TablePagination,
} from '@mui/material';

import { useTranslate } from 'src/locales';
import { CsvImportRowOutcome } from 'src/services/common.types';

import { CsvImportResult } from './import-types';

const OUTCOME_COLOR: Record<CsvImportRowOutcome, 'success' | 'info' | 'default' | 'error'> = {
  created: 'success',
  updated: 'info',
  unchanged: 'default',
  skipped: 'default',
  failed: 'error',
};

type Props = {
  results: CsvImportResult[];
  resultLabel: (result: CsvImportResult) => string;
  /** Labels a filled field by its row key or `cf:<config id>`. */
  labelFor: (field: string) => string;
  showCompanyColumn: boolean;
  /** The list the import added its records to, if one was chosen. */
  listName?: string;
};

export default function ImportResults({ results, resultLabel, labelFor, showCompanyColumn, listName }: Props) {
  const { t } = useTranslate();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const stats = useMemo(() => {
    const count = (outcome: CsvImportRowOutcome) => results.filter((result) => result.outcome === outcome).length;

    return {
      created: count('created'),
      updated: count('updated'),
      unchanged: count('unchanged'),
      notImported: count('skipped') + count('failed'),
      failed: count('failed'),
      withWarnings: results.filter((result) => result.outcome !== 'failed' && result.warnings?.length).length,
      listed: results.filter((result) => result.added_to_list).length,
    };
  }, [results]);

  const pageResults = results.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const renderDetails = (result: CsvImportResult) => (
    <Stack spacing={0.5}>
      {(result.outcome === 'failed' || result.outcome === 'skipped') && (
        <Typography variant="body2" color={result.outcome === 'failed' ? 'error.main' : 'text.secondary'}>
          {result.message}
        </Typography>
      )}

      {result.outcome === 'updated' && result.filled?.length ? (
        <Typography variant="body2">
          {t('csv_import.results.filled', {
            fields: result.filled.map((entry) => `${labelFor(entry.field)} (${entry.value})`).join(', '),
          })}
        </Typography>
      ) : null}

      {result.outcome === 'unchanged' && (
        <Typography variant="body2" color="text.secondary">
          {t('csv_import.results.unchanged_detail')}
        </Typography>
      )}

      {result.warnings?.map((warning) => (
        <Typography key={warning} variant="body2" color="warning.dark">
          {warning}
        </Typography>
      ))}
    </Stack>
  );

  return (
    <Stack spacing={2}>
      <Alert severity={stats.failed > 0 ? 'warning' : 'success'}>
        {t('csv_import.results.summary', {
          created: stats.created,
          updated: stats.updated,
          unchanged: stats.unchanged,
          notImported: stats.notImported,
          total: results.length,
        })}
        {stats.withWarnings > 0 && ` ${t('csv_import.results.with_warnings', { count: stats.withWarnings })}`}
      </Alert>

      {listName && stats.listed > 0 && (
        <Alert severity="info">{t('csv_import.results.added_to_list', { count: stats.listed, name: listName })}</Alert>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('csv_import.results.record')}</TableCell>
              {showCompanyColumn && <TableCell>{t('csv_import.results.company')}</TableCell>}
              <TableCell width={140}>{t('csv_import.results.result')}</TableCell>
              <TableCell>{t('csv_import.results.details')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageResults.map((result, index) => (
              <TableRow key={index} hover>
                <TableCell>{resultLabel(result)}</TableCell>

                {showCompanyColumn && (
                  <TableCell>
                    {result.company ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2">{result.company.name}</Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={result.company.status === 'created' ? 'info' : 'default'}
                          label={t(`csv_import.results.company_${result.company.status}`)}
                        />
                      </Stack>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                )}

                <TableCell>
                  <Chip
                    size="small"
                    color={OUTCOME_COLOR[result.outcome] ?? 'default'}
                    variant={result.outcome === 'created' || result.outcome === 'failed' ? 'filled' : 'outlined'}
                    label={t(`csv_import.results.outcome_${result.outcome}`)}
                  />
                </TableCell>

                <TableCell>{renderDetails(result)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={results.length}
        page={page}
        onPageChange={(_, next) => setPage(next)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Stack>
  );
}
